import { Components, Theme } from '@mui/material'

declare module '@mui/material/Tabs' {
  interface TabsOwnProps {
    'data-shape'?: 'line' | 'rounded'
  }
}

export const MuiTabs: Components<Omit<Theme, 'components'>>['MuiTabs'] = {
  defaultProps: {
    variant: 'scrollable',
    scrollButtons: false,
    'data-shape': 'line',
  },
  variants: [
    {
      props: { 'data-shape': 'rounded' },
      style: ({ theme }) => ({
        '& .MuiTabs-list': {
          border: 'none',
          gap: theme.spacing(2),
        },
        '& .MuiTabs-indicator': {
          display: 'none',
        },
        '& .MuiTab-root': {
          borderRadius: theme.shape['futureTheme.radius-full'],
          borderStyle: 'solid',
          borderWidth: theme.futureTheme.strokeWidth.default,
          borderColor: theme.palette.futureTheme.border.strong,
          padding: theme.spacing(1, 2.5),
          // We don't want the minHeight from lines shape
          minHeight: 'unset',
          // Hide the lines hover effect
          '::before': {
            display: 'none',
          },
          '&.Mui-selected': {
            backgroundColor: theme.palette.futureTheme.bg.interactive.active,
          },
          '&:hover': {
            backgroundColor: theme.palette.futureTheme.bg.interactive.hover,
          },
        },
      }),
    },
  ],
  styleOverrides: {
    root: {
      minHeight: 40,

      flex: '1 1 0%',
      minWidth: 0,
      maxWidth: '100%',
      width: '100%',
      overflow: 'hidden',
      position: 'relative',
    },
    indicator: ({ theme }) => ({
      backgroundColor: theme.palette.futureTheme.border.inverted,
      height: theme.futureTheme.strokeWidth.strongest,

      // Switch indicator to left
      '.MuiTabs-vertical &': {
        left: 0,
        right: 'unset',
      },
    }),
    list: ({ theme }) => ({
      borderWidth: 0,
      borderBottomWidth: theme.futureTheme.strokeWidth.default,
      borderStyle: 'solid',
      borderColor: theme.palette.futureTheme.border.subtle,
      '.MuiTabs-vertical &': {
        borderBottomWidth: 0,
        borderLeftWidth: theme.futureTheme.strokeWidth.default,
      },
    }),
  },
}

export const MuiTab: Components<Omit<Theme, 'components'>>['MuiTab'] = {
  defaultProps: {
    disableRipple: true,
    iconPosition: 'start',
  },
  styleOverrides: {
    root: ({ theme }) => ({
      minHeight: 40,
      ...theme.typography['futureTheme.body-medium'],
      textTransform: 'none',
      color: theme.palette.futureTheme.text.muted,
      '&.Mui-selected, &:hover': {
        color: theme.palette.futureTheme.text.default,
      },
      // Hack as 3.5 isn't allowed, but designs have 1 + 2.5 to support the hover overlay
      paddingLeft: `calc(${theme.spacing(1)} + ${theme.spacing(2.5)})`,
      paddingRight: `calc(${theme.spacing(1)} + ${theme.spacing(2.5)})`,
      paddingTop: theme.spacing(2),
      paddingBottom: theme.spacing(2),

      gap: theme.spacing(1.5),

      '.MuiTabs-vertical &': {
        justifyContent: 'flex-start',
        alignSelf: 'flex-start',
        minWidth: 0,
      },
      '::before': {
        content: '""',
        position: 'absolute',
        inset: `${theme.spacing(1)} ${theme.spacing(1)}`,
        borderRadius: theme.shape['futureTheme.radius-md'],
        backgroundColor: theme.palette.futureTheme.bg.invisible,
        zIndex: 0,
      },
      '&:hover::before': {
        backgroundColor: theme.palette.futureTheme.bg.interactive.hover,
      },
    }),
    icon: ({ theme }) => ({
      fontSize: 18,
      // We use gap to space the icon and label, not margin
      margin: 0,
    }),
  },
}
